import React from "react"
import "./skeleton.css"

/**
 * The loading placeholder.
 *
 * A spinner says "wait" and nothing else: the frame is empty, the page has no
 * size yet, and it jumps when content lands. A skeleton says how much is coming
 * and where, and holds the layout while it arrives.
 *
 * Deliberately dumb - a shape and a shimmer. Callers compose these into the
 * outline of whatever is loading, so the placeholder resembles the real screen
 * rather than a generic grey box. The values match the table skeleton in
 * `app-table`, so a page that loads a chart and a table does not shimmer in two
 * different greys.
 */
export const Skeleton: React.FC<{
  /** CSS width. Vary it across sibling lines, or they read as a rendering fault. */
  width?: string
  height?: string
  /** `text` for a line of copy, `block` for a card or panel, `circle` for an avatar. */
  variant?: "text" | "block" | "circle"
  className?: string
}> = ({ width = "100%", height, variant = "text", className = "" }) => {
  const radius =
    variant === "circle" ? "50%" : variant === "block" ? "8px" : "999px"
  const fallbackHeight =
    variant === "circle" ? "40px" : variant === "block" ? "100%" : "12px"

  return (
    <span
      className={`app-skeleton ${className}`}
      style={{
        width,
        height: height ?? fallbackHeight,
        borderRadius: radius,
      }}
      // Decorative: the surrounding region carries aria-busy, so a screen
      // reader is told the section is loading once rather than per bar.
      aria-hidden="true"
    />
  )
}

/**
 * A stack of lines, for a paragraph or a list of values. The last line is short
 * because real text rarely fills its final line, and an even block of bars is
 * the thing that reads as broken rather than loading.
 */
export const SkeletonLines: React.FC<{
  lines?: number
  className?: string
}> = ({ lines = 3, className = "" }) => (
  <div className={`flex flex-col gap-2 ${className}`}>
    {Array.from({ length: lines }, (_, i) => (
      <Skeleton
        key={i}
        width={i === lines - 1 ? "60%" : `${[100, 88, 94][i % 3]}%`}
      />
    ))}
  </div>
)

export default Skeleton

/* ------------------------------------------------------------- presets -- */

/**
 * How much of the screen is waiting.
 *
 * Callers were picking a bar count by eye, which produced six different
 * values across the app and two similar panels shimmering at different
 * sizes. These name the three cases that actually occur, so the choice is
 * reviewable and a change to the treatment lands everywhere at once.
 */
const SKELETON_PRESETS = {
  /** A few rows inside an existing card or list. */
  row: 3,
  /** A card body, a form section, a drawer. */
  panel: 5,
  /** A whole screen, before any of its chrome exists. */
  page: 8,
} as const

export type SkeletonSize = keyof typeof SKELETON_PRESETS

/**
 * A whole screen waiting on its first load.
 *
 * This block was copy-pasted into eight screens before it was a component.
 * The height matters as much as the bars: these replaced full-viewport
 * spinners, and a short strip of placeholder leaves the rest of the page
 * looking not-loading-but-empty, which reads as "no data" rather than "on
 * its way".
 */
export const PageSkeleton: React.FC<{
  size?: SkeletonSize
  className?: string
}> = ({ size = "page", className = "" }) => (
  <div
    className={`min-h-[60vh] w-full p-6 ${className}`}
    aria-busy="true"
  >
    <SkeletonLines lines={SKELETON_PRESETS[size]} />
  </div>
)

/**
 * A region inside a screen that already has its chrome - a card body, a
 * drawer, one section of a form. No minimum height: the surrounding layout
 * already holds the space.
 */
export const PanelSkeleton: React.FC<{
  size?: SkeletonSize
  className?: string
}> = ({ size = "panel", className = "" }) => (
  <div
    className={`w-full ${className}`}
    aria-busy="true"
  >
    <SkeletonLines lines={SKELETON_PRESETS[size]} />
  </div>
)

/**
 * A settings page: sections of labelled rows.
 *
 * The settings screens are all built the same way - `SegmentWrapper` cards,
 * each holding rows of a label beside its control - so a generic stack of
 * bars there reads as the wrong page arriving. This keeps the card chrome and
 * the label/control rhythm, at the padding those wrappers use.
 */
export const SettingsSkeleton: React.FC<{
  /** Cards to draw. */
  sections?: number
  /** Rows inside each card. */
  rows?: number
  className?: string
}> = ({ sections = 2, rows = 4, className = "" }) => (
  <div
    className={`flex w-full flex-col gap-6 ${className}`}
    aria-busy="true"
  >
    {Array.from({ length: sections }, (_, section) => (
      <div
        key={section}
        className="w-full rounded-lg border bg-white pb-8 pt-6 lg:pt-[30px]"
      >
        <div className="px-4 md:px-6 lg:px-8 xl:px-[60px]">
          <Skeleton
            width="200px"
            height="16px"
          />
          <div className="mt-2">
            <Skeleton width="min(420px, 70%)" />
          </div>
        </div>

        <div className="mt-7 flex flex-col gap-5 px-4 md:px-6 lg:px-8 xl:px-[60px]">
          {Array.from({ length: rows }, (_, row) => (
            <div
              key={row}
              className="grid grid-cols-1 items-center gap-4 sm:grid-cols-3"
            >
              {/* Label, then the control it belongs to - the real row shape. */}
              <Skeleton width={`${[72, 58, 66, 80, 62][row % 5]}%`} />
              <div className="sm:col-span-2">
                <Skeleton
                  variant="block"
                  height="38px"
                />
              </div>
            </div>
          ))}
        </div>
      </div>
    ))}
  </div>
)

/**
 * A wait where nothing about the coming page can be drawn honestly.
 *
 * The white-label boot is the case this exists for: it fetches the branding,
 * so it cannot show branded chrome, and it sits in front of login as well as
 * the dashboard, so it cannot show the app shell. A thin bar at the top of the
 * viewport says work is happening without claiming what is coming, and unlike
 * a centred spinner it does not read as a flicker when the wait is brief.
 */
export const BootProgress = () => (
  <div
    className="app-boot-progress"
    role="status"
    aria-label="Loading"
  />
)

/**
 * A chart while its data loads: bars of differing heights on a baseline, with room for the
 * legend under them.
 *
 * Flat lines where a chart goes read as a paragraph arriving, so the card changed shape
 * entirely the moment the real content landed.
 */
export const ChartSkeleton: React.FC<{
  bars?: number
  height?: string
  className?: string
}> = ({ bars = 8, height = "200px", className = "" }) => (
  <div
    className={`flex w-full flex-col gap-3 ${className}`}
    aria-busy="true"
  >
    <div
      className="flex items-end justify-between gap-2 sm:gap-3"
      style={{ height }}
    >
      {Array.from({ length: bars }, (_, i) => (
        <Skeleton
          key={i}
          variant="block"
          width="100%"
          // A repeating run of heights: a chart is uneven, and bars all one height
          // read as a loading fault rather than as a chart on its way.
          height={`${[62, 84, 48, 96, 70, 55, 88, 42][i % 8]}%`}
        />
      ))}
    </div>

    <div className="flex items-center justify-center gap-4">
      {Array.from({ length: 2 }, (_, i) => (
        <div
          key={i}
          className="flex items-center gap-2"
        >
          <Skeleton
            variant="circle"
            width="12px"
            height="12px"
          />
          <Skeleton width="96px" />
        </div>
      ))}
    </div>
  </div>
)

/**
 * The admin dashboard, in grey: the currency picker, the row of figures, the payroll chart
 * beside the profile card, the actions strip, and the attrition chart.
 *
 * This is what a cold load and an account switch stand in for, and both land on the
 * dashboard. The generic shell drawn before -- four plain tiles over a six-row table -- was a
 * screen the product does not have, so the handover was a jump to something else entirely.
 */
export const DashboardSkeleton: React.FC<{ className?: string }> = ({
  className = "",
}) => (
  <div
    className={`flex w-full flex-col ${className}`}
    aria-busy="true"
  >
    {/* Currency picker, right-aligned as on the page. */}
    <div className="mb-6 flex items-center justify-start">
      <Skeleton
        variant="block"
        width="117px"
        height="32px"
      />
    </div>

    {/* Figures: icon, label, value. */}
    <div className="mb-6 flex flex-col gap-4 lg:mb-8 lg:flex-row">
      {Array.from({ length: 4 }, (_, i) => (
        <div
          key={i}
          className="grow rounded-xl border bg-white p-4 lg:p-5"
        >
          <div className="flex gap-3">
            <Skeleton
              variant="circle"
              width="44px"
              height="44px"
            />
            <div className="flex w-full flex-col gap-2">
              <Skeleton width="55%" />
              <Skeleton
                width="40%"
                height="24px"
              />
            </div>
          </div>
        </div>
      ))}
    </div>

    {/* Payroll chart, two thirds, beside the profile card. */}
    <div className="mb-6 grid gap-6 lg:mb-8 xl:grid-cols-3 xl:gap-4">
      <div className="flex flex-col xl:col-span-2">
        <div className="mb-4">
          <Skeleton
            width="180px"
            height="16px"
          />
        </div>
        <div className="grow rounded-xl border bg-white p-4 lg:p-5">
          <ChartSkeleton bars={8} />
        </div>
      </div>

      <div className="flex flex-col">
        <div className="mb-4">
          <Skeleton
            width="120px"
            height="16px"
          />
        </div>
        <div className="flex grow flex-col items-center gap-3 rounded-xl border bg-white p-5">
          <Skeleton
            variant="circle"
            width="64px"
            height="64px"
          />
          <Skeleton width="60%" />
          <Skeleton width="40%" />
          <div className="mt-2 w-full">
            <SkeletonLines lines={3} />
          </div>
        </div>
      </div>
    </div>

    {/* Actions strip. */}
    <div className="mb-6 rounded-xl border bg-white p-5 lg:mb-8">
      <Skeleton
        width="140px"
        height="16px"
      />
      <div className="mt-4 grid gap-4 sm:grid-cols-2 lg:grid-cols-4">
        {Array.from({ length: 4 }, (_, i) => (
          <Skeleton
            key={i}
            variant="block"
            height="72px"
          />
        ))}
      </div>
    </div>

    {/* Attrition chart, at the height the real one holds. */}
    <div className="grid grid-cols-1 gap-5 lg:grid-cols-3">
      <div className="lg:col-span-2">
        <div className="mb-4">
          <Skeleton
            width="200px"
            height="16px"
          />
        </div>
        <div className="h-[336px] w-full rounded-5px bg-white px-5 py-4 shadow-card">
          <ChartSkeleton
            bars={12}
            height="240px"
          />
        </div>
      </div>

      <div>
        <div className="mb-4">
          <Skeleton
            width="140px"
            height="16px"
          />
        </div>
        <div className="flex h-[336px] flex-col gap-4 rounded-5px bg-white px-5 py-4 shadow-card">
          <SkeletonLines lines={6} />
        </div>
      </div>
    </div>
  </div>
)

/* --------------------------------------------------------------- shapes -- */
//
// Placeholders that promise a specific layout, for loaders that stand in for
// real content. A generic stack of lines matches no page, so the handover to
// the loaded screen reads as a jump; these match the structures pages are
// actually built from, so it reads as the content filling in.

const FIELD_LABEL_WIDTHS = ["96px", "132px", "84px", "118px", "104px", "140px"]

/**
 * A form, in `FormSectionWrapper`'s layout: the title and subtitle over a
 * bordered body of labelled fields, and the footer with its buttons. Fields sit
 * in the two-column grid most forms use.
 */
export const FormSkeleton: React.FC<{
  fields?: number
  columns?: 1 | 2
  /** Draw the title and subtitle header. Off where the page already shows it. */
  header?: boolean
  footer?: boolean
  className?: string
}> = ({
  fields = 6,
  columns = 2,
  header = true,
  footer = true,
  className = "",
}) => (
  <div
    className={`flex flex-col w-full gap-7 bg-white max-w-screen-2xl pt-6 lg:pt-[30px] ${className}`}
    aria-busy="true"
  >
    {header ? (
      <div className="flex flex-col gap-2 px-4 md:px-6 lg:px-8 xl:px-[60px]">
        <Skeleton
          width="220px"
          height="16px"
        />
        <Skeleton width="360px" />
      </div>
    ) : null}
    <div className="border-t py-6 pb-2">
      <div
        className={`grid gap-x-6 gap-y-5 px-4 md:px-6 xl:px-[60px] ${
          columns === 2 ? "grid-cols-1 md:grid-cols-2" : "grid-cols-1"
        }`}
      >
        {Array.from({ length: fields }, (_, i) => (
          <div
            key={i}
            className="flex flex-col gap-2"
          >
            <Skeleton
              width={FIELD_LABEL_WIDTHS[i % FIELD_LABEL_WIDTHS.length]}
            />
            <Skeleton
              variant="block"
              height="40px"
            />
          </div>
        ))}
      </div>
      {footer ? (
        <div className="mt-6 flex justify-end gap-3 border-t px-4 py-3 md:px-6 xl:px-[60px]">
          <Skeleton
            variant="block"
            width="96px"
            height="40px"
          />
          <Skeleton
            variant="block"
            width="120px"
            height="40px"
          />
        </div>
      ) : null}
    </div>
  </div>
)

/**
 * A stack of bordered cards, each a title over a few lines: question previews,
 * review items, collapsible panels. Set `lines={0}` for header-only rows.
 */
export const CardListSkeleton: React.FC<{
  items?: number
  lines?: number
  /** A toolbar row above the list: a filter on the left, an action on the right. */
  toolbar?: boolean
  /** A tab strip above the list, with this many tabs. */
  tabs?: number
  className?: string
}> = ({ items = 3, lines = 2, toolbar = false, tabs = 0, className = "" }) => (
  <div
    className={`flex flex-col gap-3 ${className}`}
    aria-busy="true"
  >
    {toolbar ? (
      <div className="flex items-center justify-between gap-4 rounded-lg bg-white px-4 py-3">
        <Skeleton
          variant="block"
          width="200px"
          height="36px"
        />
        <Skeleton
          variant="block"
          width="120px"
          height="36px"
        />
      </div>
    ) : null}
    {tabs > 0 ? (
      <div className="flex gap-6 border-b border-[#e2e8f0] pb-3">
        {Array.from({ length: tabs }, (_, i) => (
          <Skeleton
            key={i}
            width={`${[72, 88, 64, 96][i % 4]}px`}
          />
        ))}
      </div>
    ) : null}
    {Array.from({ length: items }, (_, i) => (
      <div
        key={i}
        className="flex flex-col gap-3 rounded-lg border border-[#e2e8f0] bg-white p-4"
      >
        <div className="flex items-center justify-between gap-4">
          <Skeleton width={`${[42, 56, 36, 48][i % 4]}%`} />
          <Skeleton
            variant="block"
            width="16px"
            height="16px"
          />
        </div>
        {lines > 0 ? <SkeletonLines lines={lines} /> : null}
      </div>
    ))}
  </div>
)

/**
 * A person's or record's detail page: the avatar, name and status over a tab
 * bar, then the body of the active tab.
 */
export const ProfileSkeleton: React.FC<{
  tabs?: number
  lines?: number
  className?: string
}> = ({ tabs = 4, lines = 6, className = "" }) => (
  <div
    className={`flex flex-col gap-6 bg-white p-4 ${className}`}
    aria-busy="true"
  >
    <div className="flex items-center gap-3">
      <Skeleton
        variant="circle"
        width="60px"
        height="60px"
      />
      <div className="flex flex-col gap-2">
        <Skeleton
          width="240px"
          height="24px"
        />
        <Skeleton width="140px" />
      </div>
    </div>
    {tabs > 0 ? (
      <div className="flex gap-6 border-b border-[#e2e8f0] pb-3">
        {Array.from({ length: tabs }, (_, i) => (
          <Skeleton
            key={i}
            width={`${[64, 80, 72, 92, 68][i % 5]}px`}
          />
        ))}
      </div>
    ) : null}
    <SkeletonLines lines={lines} />
  </div>
)

/**
 * A vertical timeline of steps, as antd's Timeline draws it: a dot on the rail,
 * a short status line, the step heading, and the bordered card holding its
 * detail and action.
 */
export const TimelineSkeleton: React.FC<{
  items?: number
  className?: string
}> = ({ items = 5, className = "" }) => (
  <div
    className={`flex flex-col ${className}`}
    aria-busy="true"
  >
    {Array.from({ length: items }, (_, i) => (
      <div
        key={i}
        className="flex gap-4"
      >
        <div className="flex flex-col items-center">
          <Skeleton
            variant="circle"
            width="24px"
            height="24px"
          />
          {i < items - 1 ? <div className="w-px grow bg-[#e2e8f0]" /> : null}
        </div>
        <div className="flex grow flex-col gap-2 pb-6">
          <Skeleton width="88px" />
          <Skeleton
            width={`${[180, 220, 160, 200, 240][i % 5]}px`}
            height="18px"
          />
          <div className="mt-1 flex items-center justify-between gap-4 rounded-lg border border-[#e2e8f0] p-4">
            <div className="flex flex-col gap-2">
              <Skeleton width="200px" />
              <Skeleton width="120px" />
            </div>
            <Skeleton
              variant="block"
              width="96px"
              height="36px"
            />
          </div>
        </div>
      </div>
    ))}
  </div>
)

/**
 * A side navigation of steps beside the active step's form, the layout the
 * onboarding sections use.
 */
export const SteppedFormSkeleton: React.FC<{
  steps?: number
  fields?: number
  /** The active step's body, where it is not a form (a list, a table). */
  content?: React.ReactNode
  className?: string
}> = ({ steps = 4, fields = 6, content, className = "" }) => (
  <div
    className={`flex ${className}`}
    aria-busy="true"
  >
    <div className="hidden lg:flex shrink-0 flex-col gap-5 bg-white border px-4 py-6 lg:min-w-[200px] xl:min-w-[220px]">
      {Array.from({ length: steps }, (_, i) => (
        <div
          key={i}
          className="flex items-center gap-3"
        >
          <Skeleton
            variant="circle"
            width="20px"
            height="20px"
          />
          <Skeleton width={`${[96, 120, 84, 108][i % 4]}px`} />
        </div>
      ))}
    </div>
    {content ? (
      <div className="grow">{content}</div>
    ) : (
      <FormSkeleton
        fields={fields}
        className="grow"
      />
    )}
  </div>
)

/**
 * The centred result card used by the auth and verification screens: the
 * status icon, a heading, a line of explanation and the button to continue.
 */
export const AuthCardSkeleton: React.FC<{ className?: string }> = ({
  className = "",
}) => (
  <div
    className={`flex min-h-full flex-col items-center justify-center gap-4 ${className}`}
    aria-busy="true"
  >
    <Skeleton
      variant="circle"
      width="87px"
      height="87px"
    />
    <Skeleton
      width="260px"
      height="24px"
    />
    <div className="flex w-full max-w-[360px] flex-col items-center gap-2">
      <Skeleton width="100%" />
      <Skeleton width="70%" />
    </div>
    <Skeleton
      variant="block"
      width="200px"
      height="44px"
      className="mt-2"
    />
  </div>
)

/**
 * A printable document such as an invoice: the print action above a sheet
 * holding the header, the billed party, the line items and the total.
 */
export const DocumentSkeleton: React.FC<{
  lineItems?: number
  className?: string
}> = ({ lineItems = 4, className = "" }) => (
  <div
    className={`flex flex-col gap-4 ${className}`}
    aria-busy="true"
  >
    <div className="flex justify-end">
      <Skeleton
        variant="block"
        width="104px"
        height="40px"
      />
    </div>
    <div className="flex flex-col gap-8 rounded-lg border border-[#e2e8f0] bg-white p-6 lg:p-10">
      <div className="flex items-start justify-between gap-6">
        <Skeleton
          variant="block"
          width="120px"
          height="40px"
        />
        <div className="flex flex-col items-end gap-2">
          <Skeleton width="140px" />
          <Skeleton width="100px" />
        </div>
      </div>
      <div className="flex flex-col gap-2">
        <Skeleton width="80px" />
        <Skeleton width="220px" />
        <Skeleton width="180px" />
      </div>
      <div className="flex flex-col">
        {Array.from({ length: lineItems }, (_, i) => (
          <div
            key={i}
            className="flex items-center justify-between gap-6 border-b border-[#e2e8f0] py-3"
          >
            <Skeleton width={`${[38, 52, 44, 30][i % 4]}%`} />
            <Skeleton width="80px" />
          </div>
        ))}
      </div>
      <div className="flex justify-end">
        <Skeleton
          width="160px"
          height="20px"
        />
      </div>
    </div>
  </div>
)

/**
 * A table whose columns are not known to the placeholder: reports and
 * summaries that build their columns from the data. A header band over rows of
 * cells, with an optional toolbar of filters above.
 */
export const TableSkeleton: React.FC<{
  rows?: number
  columns?: number
  toolbar?: boolean
  className?: string
}> = ({ rows = 8, columns = 5, toolbar = false, className = "" }) => (
  <div
    className={`flex flex-col gap-4 ${className}`}
    aria-busy="true"
  >
    {toolbar ? (
      <div className="flex flex-wrap items-center justify-between gap-3">
        <div className="flex gap-3">
          <Skeleton
            variant="block"
            width="180px"
            height="36px"
          />
          <Skeleton
            variant="block"
            width="140px"
            height="36px"
          />
        </div>
        <Skeleton
          variant="block"
          width="112px"
          height="36px"
        />
      </div>
    ) : null}
    <div className="overflow-hidden rounded-lg border border-[#e2e8f0] bg-white">
      <div
        className="grid gap-4 border-b border-[#e2e8f0] bg-[#f8fafc] px-4 py-3"
        style={{ gridTemplateColumns: `repeat(${columns}, minmax(0, 1fr))` }}
      >
        {Array.from({ length: columns }, (_, c) => (
          <Skeleton
            key={c}
            width={`${[60, 72, 54, 66, 48][c % 5]}%`}
          />
        ))}
      </div>
      {Array.from({ length: rows }, (_, r) => (
        <div
          key={r}
          className="grid h-14 items-center gap-4 border-b border-[#e2e8f0] px-4 last:border-b-0"
          style={{ gridTemplateColumns: `repeat(${columns}, minmax(0, 1fr))` }}
        >
          {Array.from({ length: columns }, (_, c) => (
            <Skeleton
              key={c}
              width={`${[78, 64, 86, 58, 70][(r + c) % 5]}%`}
            />
          ))}
        </div>
      ))}
    </div>
  </div>
)

/**
 * Label and value pairs down a panel: request details in a view modal, account
 * details in a funding drawer.
 */
export const KeyValueSkeleton: React.FC<{
  rows?: number
  className?: string
}> = ({ rows = 6, className = "" }) => (
  <div
    className={`flex flex-col ${className}`}
    aria-busy="true"
  >
    {Array.from({ length: rows }, (_, i) => (
      <div
        key={i}
        className="flex items-center justify-between gap-6 border-b border-[#f1f5f9] py-3 last:border-b-0"
      >
        <Skeleton width={`${[28, 36, 24, 32][i % 4]}%`} />
        <Skeleton width={`${[40, 30, 46, 34][i % 4]}%`} />
      </div>
    ))}
  </div>
)

/** A month grid: the navigation header over weeks of day cells. */
export const CalendarSkeleton: React.FC<{
  weeks?: number
  className?: string
}> = ({ weeks = 5, className = "" }) => (
  <div
    className={`flex flex-col gap-4 bg-white p-4 ${className}`}
    aria-busy="true"
  >
    <div className="flex items-center justify-between gap-4">
      <div className="flex items-center gap-3">
        <Skeleton
          variant="circle"
          width="32px"
          height="32px"
        />
        <Skeleton width="160px" />
        <Skeleton
          variant="circle"
          width="32px"
          height="32px"
        />
      </div>
      <Skeleton
        variant="block"
        width="160px"
        height="36px"
      />
    </div>
    <div className="grid grid-cols-7 gap-px overflow-hidden rounded-lg border border-[#e2e8f0] bg-[#e2e8f0]">
      {Array.from({ length: weeks * 7 }, (_, i) => (
        <div
          key={i}
          className="flex h-24 flex-col gap-2 bg-white p-2"
        >
          <Skeleton width="20px" />
          {i % 5 === 1 ? <Skeleton width="80%" /> : null}
        </div>
      ))}
    </div>
  </div>
)
